/**
 * Client-side parsing for Address Book import (CSV and vCard).
 * Output rows: { row, name, email, group, status: "ok" | "invalid" | "duplicate" | "existing", message }
 */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const isValidEmail = (email) => EMAIL_RE.test(String(email || "").trim());

/** RFC 4180-style CSV parser (quotes, escaped quotes, CRLF). */
export const parseCsv = (text) => {
    const rows = [];
    let row = [], field = "", quoted = false;
    const src = String(text || "").replace(/^﻿/, "");
    for(let i = 0; i < src.length; i++){
        const ch = src[i];
        if(quoted){
            if(ch === '"'){
                if(src[i+1] === '"'){ field += '"'; i++; } else { quoted = false; }
            }else{ field += ch; }
            continue;
        }
        if(ch === '"'){ quoted = true; }
        else if(ch === "," || ch === ";"){ row.push(field); field = ""; }
        else if(ch === "\n" || ch === "\r"){
            if(ch === "\r" && src[i+1] === "\n"){ i++; }
            row.push(field); field = "";
            if(row.some(c => c.trim() !== "")){ rows.push(row); }
            row = [];
        }else{ field += ch; }
    }
    row.push(field);
    if(row.some(c => c.trim() !== "")){ rows.push(row); }
    return rows;
};

/** Guesses Name/Email/Group columns from a header row. -1 = not present. */
export const guessCsvMapping = (header) => {
    const find = (re) => header.findIndex(h => re.test(String(h).trim()));
    let email = find(/^e-?mail( address)?$/i);
    if(email === -1){ email = find(/mail/i); }
    let name = find(/^(full )?name$|display name/i);
    if(name === -1){ name = find(/name/i); }
    const group = find(/^(group|groups|category|list)$/i);
    return { hasHeader: email !== -1, name, email: email === -1 ? 0 : email, group };
};

export const csvToRecords = (rows, mapping) => {
    const body = mapping.hasHeader ? rows.slice(1) : rows;
    const pick = (r, i) => (i >= 0 && i < r.length ? String(r[i]).trim() : "");
    return body.map((r, i) => ({
        row: i + 1 + (mapping.hasHeader ? 1 : 0),
        name: pick(r, mapping.name),
        email: pick(r, mapping.email).toLowerCase(),
        group: pick(r, mapping.group)
    }));
};

const unfold = (text) => String(text || "").replace(/\r\n|\r/g, "\n").replace(/\n[ \t]/g, "");
const vcardValue = (line) => {
    const idx = line.indexOf(":");
    return idx === -1 ? "" : line.slice(idx + 1).replace(/\\,/g, ",").replace(/\\;/g, ";").replace(/\\n/gi, " ").trim();
};

/** vCard 2.1/3.0/4.0: one record per EMAIL found in each card. */
export const parseVcard = (text) => {
    const records = [];
    let card = null, n = 0;
    unfold(text).split("\n").forEach(raw => {
        const line = raw.trim();
        const key = line.split(/[;:]/)[0].toUpperCase().replace(/^ITEM\d+\./, "");
        if(key === "BEGIN" && /vcard/i.test(line)){ card = { fn: "", n: "", emails: [], groups: "" }; return; }
        if(!card){ return; }
        if(key === "END"){
            n++;
            const name = card.fn || card.n;
            card.emails.forEach(email => records.push({ row: n, name, email: email.toLowerCase(), group: card.groups }));
            if(card.emails.length === 0){ records.push({ row: n, name, email: "", group: card.groups }); }
            card = null;
            return;
        }
        if(key === "FN"){ card.fn = vcardValue(line); }
        else if(key === "N" && !card.n){
            const parts = vcardValue(line).split(";");
            card.n = [parts[1], parts[0]].filter(Boolean).join(" ").trim();
        }
        else if(key === "EMAIL"){ const v = vcardValue(line); if(v){ card.emails.push(v); } }
        else if(key === "CATEGORIES"){ card.groups = vcardValue(line).split(",")[0].trim(); }
    });
    return records;
};

/** Flags invalid rows, duplicates within the file, and addresses already in the address book. */
export const classifyRecords = (records, existingEmails) => {
    const existing = new Set((existingEmails || []).map(e => String(e).toLowerCase()));
    const seen = new Set();
    return records.map(r => {
        if(!isValidEmail(r.email)){ return { ...r, status: "invalid", message: r.email ? "Invalid email" : "Missing email" }; }
        if(seen.has(r.email)){ return { ...r, status: "duplicate", message: "Duplicate in file" }; }
        seen.add(r.email);
        if(existing.has(r.email)){ return { ...r, status: "existing", message: "Already a contact" }; }
        return { ...r, status: "ok", message: "" };
    });
};
